import { createContext, ReactElement, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { LOCAL_STORAGE_DURATION, LOCAL_STORAGE_START } from '../constants/localstorage.constants';
import { getFromLocalStorage, removeFromLocalStorage, setToLocalStorage } from '../utils/storage.util';

type TimerContextType = {
	timeLeft: number;
	isRunning: boolean;
	startTimer: (duration: number) => void;
	stopTimer: () => void;
	resetTimer: () => void;
};

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }: {children: ReactNode}): ReactElement => {
	const [timeLeft, setTimeLeft] = useState(0);
	const [duration, setDuration] = useState(0);
	const [isRunning, setIsRunning] = useState(false);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	// Restore timer if localStorage has a start date
	useEffect((): void => {
		const savedStart: string | null = getFromLocalStorage(LOCAL_STORAGE_START);
		const savedDuration: string | null = getFromLocalStorage(LOCAL_STORAGE_DURATION);

		if (savedStart && savedDuration) {
			const start: number = new Date(savedStart).getTime();
			const now: number = Date.now();
			const duration: number = parseInt(savedDuration, 10);
			const elapsed: number = Math.floor((now - start) / 1000);
			const remaining: number = duration - elapsed;

			if (remaining > 0) {
				setTimeLeft(remaining);
				setIsRunning(true);
				startCountdown(remaining);
			} else {
				resetTimer();
			}
		}
	}, []);

	const startCountdown = (initialTime: number): void => {
		clearInterval(intervalRef.current!);
		intervalRef.current = setInterval((): void => {
			setTimeLeft((prev: number): number => {
				if (prev <= 1) {
					clearInterval(intervalRef.current!);
					resetTimer();
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	};

	const startTimer = (duration: number): void => {
		const now = new Date();
		setToLocalStorage(LOCAL_STORAGE_START, now.toISOString());
		setToLocalStorage(LOCAL_STORAGE_DURATION, duration.toString());
		setTimeLeft(duration);
		setIsRunning(true);
		startCountdown(duration);
	};

	const stopTimer = (): void => {
		clearInterval(intervalRef.current!);
		setIsRunning(false);
		setTimeLeft(0);
		removeFromLocalStorage(LOCAL_STORAGE_START);
		removeFromLocalStorage(LOCAL_STORAGE_DURATION);
	};

	const resetTimer = (): void => {
		clearInterval(intervalRef.current!);
		setIsRunning(false);
		setTimeLeft(0);
		removeFromLocalStorage(LOCAL_STORAGE_START);
		removeFromLocalStorage(LOCAL_STORAGE_DURATION);
	};

	return (
		<TimerContext.Provider value={{ timeLeft, isRunning, startTimer, stopTimer, resetTimer }}>
			{children}
		</TimerContext.Provider>
	);
};

export const useTimer = (): TimerContextType => {
	const context: TimerContextType | undefined = useContext(TimerContext);
	if (!context) {
		throw new Error('useTimer must be used within a TimerProvider');
	}
	return context;
};
